import Image from "next/image";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import { memo, useEffect } from "react";

import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import EmptyBlock from "@components/cms/EmptyBlock/EmptyBlock";
import { useStore } from "@hooks/useZustandProvider";
// import { useMousePosition } from "@hooks/useMousePosition";
import Editor from "@ui-kit/cms/Editor/Editor";
import Container from "@ui-kit/Container/Container";
import Tab from "@ui-kit/Tabs/Tab";
import TabContainer from "@ui-kit/Tabs/TabContainer";
import TabContent from "@ui-kit/Tabs/TabContent";
import Tabs from "@ui-kit/Tabs/Tabs";
import TabsPanel from "@ui-kit/Tabs/TabsPanel";
import TooltipInput from "@ui-kit/TooltipInput/TooltipInput";

import s from "./ContactsBlock.module.scss";
import { tabTemplate, collectionType } from "./datas";

const ContactsBlock = ({ settings, updateField, data, index }) => {
  const { t } = useTranslation(["common"]);
  // const { isWidget } = useMousePosition();

  const { cms, updateCollectionType } = useStore();
  const { isWidget } = cms.page;
  const { isWidgetEditing } = cms.widget;
  const { blockIndex } = cms.page;
  const { collections, countriesData, title } = data;
  const { isEditMode } = cms.uiOptions;
  const { blockType } = settings.common;
  const { items } = collections[collectionType];

  const updateCountryField = (country, field) => {
    updateField({
      name: "countriesData",
      value: {
        ...countriesData,
        countries: {
          ...countriesData.countries,
          [country]: {
            ...countriesData.countries[country],
            [field.name]: field.value,
          },
        },
      },
    });
  };

  const updateLink = (country, link, key) => {
    updateField({
      name: "countriesData",
      value: {
        ...countriesData,
        countries: {
          ...countriesData.countries,
          [country]: {
            ...countriesData.countries[country],
            links: {
              ...tabTemplate.links,
              ...countriesData.countries[country].links,
              [key]: link,
            },
          },
        },
      },
    });
  };

  const cursorNotice = "Double click to edit countries list";

  const handleClickToEdit = (e) => {
    switch (e.detail) {
      case 2: // double click
        updateCollectionType(e);
    }
  };

  const filteredItems = items.filter((item) => {
    return typeof item.isShow === "boolean" ? item.isShow : true;
  });

  const linksData = [
    {
      text: t("booking, payment"),
      order: 4,
    },
    {
      text: t("change, refund"),
      order: 1,
    },
    {
      text: t("campaigns, vouchers"),
      order: 5,
    },
    {
      text: `${t("travelling with")}...`,
      order: 6,
    },
    {
      text: t("lost your belongings?"),
      order: 2,
    },
    {
      text: t("general feedback"),
      order: 3,
    },
  ];

  useEffect(() => {
    if (isEditMode && blockIndex === index) {
      const countries = {};

      Object.entries(countriesData.countries)
        .filter(([key]) => filteredItems.some((i) => i.code === key))
        .forEach(([key, val]) => {
          countries[key] = val;
        });
      updateField({
        name: "countriesData",
        value: {
          ...countriesData,
          countries,
        },
      });
    }
  }, [filteredItems.length]);

  return (
    <Container className={s.contactsBlock}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          {isEditMode && !filteredItems.length && (
            <EmptyBlock
              cursorNotice={cursorNotice}
              collectionType={collectionType}
              onClick={handleClickToEdit}
            />
          )}
          <Tabs className={s.tabs}>
            <TabsPanel
              {...(isEditMode && !(!isWidgetEditing && isWidget)
                ? {
                    "data-cursor-notice": cursorNotice,
                    "data-collection-type": collectionType,
                    onClick: handleClickToEdit,
                  }
                : {})}
            >
              {filteredItems.map((item) => (
                <Tab
                  className={s.tab}
                  text={item.label}
                  key={item.countryCode}
                />
              ))}
            </TabsPanel>
            <TabContainer>
              {filteredItems.map((country) => {
                const { title, subTitle, links, phoneNumber, email, content } =
                  {
                    ...tabTemplate,
                    ...countriesData.countries?.[country.code],
                  };
                return (
                  <TabContent key={country.code} className={s.tabContent}>
                    <h3 className={s.tabTitle}>
                      <Editor
                        field={{ title }}
                        onChange={(field) => {
                          updateCountryField(country.code, field);
                        }}
                        onlyText
                      />
                    </h3>
                    <h5 className={s.tabSubTitle}>
                      <Editor
                        field={{ subTitle }}
                        onChange={(field) => {
                          updateCountryField(country.code, field);
                        }}
                        onlyText
                      />
                    </h5>
                    <div className={s.links}>
                      {Object.entries(links).map(([key, value], index) => {
                        const { text, order } = linksData[index];
                        return (
                          <TooltipInput
                            key={index}
                            style={{ order }}
                            className={s.contentClass}
                            placeholder="Link"
                            value={value}
                            onChange={(e) => {
                              updateLink(country.code, e.target.value, key);
                            }}
                            target={
                              isEditMode ? (
                                <div className={s.item}>
                                  <Image
                                    src={`/static/images/svg/contacts-${
                                      index + 1
                                    }.svg`}
                                    height={24}
                                    width={32}
                                    alt=""
                                  />
                                  <h5>{text}</h5>
                                </div>
                              ) : (
                                !!value && (
                                  <Link href={value} className={s.item}>
                                    <Image
                                      src={`/static/images/svg/contacts-${
                                        index + 1
                                      }.svg`}
                                      height={24}
                                      width={32}
                                      alt=""
                                    />
                                    <h5>{text}</h5>
                                  </Link>
                                )
                              )
                            }
                          />
                        );
                      })}
                    </div>
                    <div className={s.info}>
                      <div className={s.block}>
                        <div className={s.key}>{t("info phone")}</div>
                        <h5 className={s.value}>
                          <Editor
                            field={{ phoneNumber }}
                            onChange={(field) => {
                              updateCountryField(country.code, field);
                            }}
                            onlyText
                          />
                        </h5>
                      </div>
                      <div className={s.block}>
                        <div className={s.key}>{t("email")}</div>
                        <h5 className={s.value}>
                          <Editor
                            field={{ email }}
                            onChange={(field) => {
                              updateCountryField(country.code, field);
                            }}
                            onlyText
                          />
                        </h5>
                      </div>
                    </div>
                    <div className={s.address}>
                      <Editor
                        field={{ content }}
                        onChange={(field) => {
                          updateCountryField(country.code, field);
                        }}
                      />
                    </div>
                  </TabContent>
                );
              })}
            </TabContainer>
          </Tabs>
        </section>
      </Container>
    </Container>
  );
};

export default memo(ContactsBlock);
